/**
 * Hard-delete confirmation for an advertiser page. Children are unlinked
 * first; the campaign pages this hub's sync created can optionally be revoked
 * too (clients of those only ever held the ADVERTISER passcode).
 */

import React, { useEffect, useState } from 'react';
import { Button, Modal } from '../../ui';
import {
  AdvertiserChildPage,
  AdvertiserReportPage,
  deleteAdvertiserPage,
  getAdvertiserPage,
} from '../../../services/advertiserReportsApi';
import type { ApiError } from '../../../services/api';
import { Notify, fieldStyle, labelStyle, plural } from './shared';

interface AdvertiserDeleteModalProps {
  page: AdvertiserReportPage;
  onClose: () => void;
  onDeleted: () => void;
  notify: Notify;
}

const AdvertiserDeleteModal: React.FC<AdvertiserDeleteModalProps> = ({ page, onClose, onDeleted, notify }) => {
  const [children, setChildren] = useState<AdvertiserChildPage[] | null>(null);
  const [childrenError, setChildrenError] = useState<string | null>(null);
  const [revokeSyncCreated, setRevokeSyncCreated] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getAdvertiserPage(page.slug)
      .then((d) => {
        if (!cancelled) setChildren(d.children);
      })
      .catch((err) => {
        if (!cancelled) setChildrenError(err?.message || 'Could not load the linked campaign pages');
      });
    return () => {
      cancelled = true;
    };
  }, [page.slug]);

  const syncCreated = (children ?? []).filter((c) => c.createdVia === 'advertiser-sync' && !c.revokedAt);
  const linkedCount = children?.length ?? 0;

  const run = async () => {
    if (confirmText !== page.slug) return;
    setDeleting(true);
    try {
      const result = await deleteAdvertiserPage(page.slug, { revokeSyncCreatedChildren: revokeSyncCreated });
      notify(
        'success',
        'Advertiser page deleted',
        `${plural(result.unlinked, 'campaign page')} unlinked${result.revoked > 0 ? `, ${result.revoked} revoked` : ''}. The URL now shows “not found.”`,
      );
      onDeleted();
      onClose();
    } catch (e: unknown) {
      const err = e as ApiError;
      notify('error', err?.code === 'unlink_failed' ? 'Nothing was deleted' : 'Delete failed', err?.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      eyebrow="Advertiser pages"
      title={`Delete — ${page.clientLabel}`}
      width={540}
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={run} disabled={deleting || confirmText !== page.slug}>
            {deleting ? 'Deleting…' : 'Delete advertiser page forever'}
          </Button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13 }}>
        <div style={{ color: 'var(--danger)', fontWeight: 600 }}>
          This permanently deletes the advertiser page and its URL — clients will see “not found” immediately. It cannot be
          undone.
        </div>

        {children === null && !childrenError && <div style={{ color: 'var(--fg-4)' }}>Checking the linked campaign pages…</div>}
        {childrenError && <div style={{ color: 'var(--warning)' }}>{childrenError} — the delete still unlinks them.</div>}

        {children !== null && syncCreated.length > 0 ? (
          <div
            style={{
              background: 'var(--warning-soft)',
              color: 'var(--fg-1)',
              borderRadius: 'var(--radius-md)',
              padding: '10px 12px',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <div>
              These {syncCreated.length} campaign page{syncCreated.length === 1 ? ' was' : 's were'} created by this advertiser
              page; clients only have the advertiser passcode. Prefer <strong>Revoke</strong> (reversible), or copy each page’s
              passcode from Manage first.
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5 }}>
              {syncCreated.map((c) => (
                <li key={c.slug}>
                  {c.clientLabel} <span style={{ color: 'var(--fg-4)' }}>· /campaigns/{c.slug}/report</span>
                </li>
              ))}
            </ul>
            <label style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <input type="checkbox" checked={revokeSyncCreated} onChange={(e) => setRevokeSyncCreated(e.target.checked)} />
              Also revoke {syncCreated.length === 1 ? 'this' : 'these'} sync-created campaign page{syncCreated.length === 1 ? '' : 's'}
            </label>
          </div>
        ) : children !== null ? (
          <div style={{ color: 'var(--fg-3)' }}>
            {linkedCount > 0
              ? `No campaign pages were created by this advertiser page. The ${plural(linkedCount, 'linked page')} stay${linkedCount === 1 ? 's' : ''} as standalone campaign page${linkedCount === 1 ? '' : 's'} with ${linkedCount === 1 ? 'its' : 'their'} own passcode.`
              : 'No campaign pages are linked to this advertiser page.'}
          </div>
        ) : null}

        <div style={{ color: 'var(--fg-3)' }}>
          If you might need it again, use <strong>Revoke</strong> instead — that keeps the page and lets you restore it later.
        </div>
        <div>
          <label style={labelStyle}>
            Type the slug to confirm: <code>{page.slug}</code>
          </label>
          <input style={fieldStyle} value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder={page.slug} />
        </div>
      </div>
    </Modal>
  );
};

export default AdvertiserDeleteModal;
